import { create } from 'zustand';

export type ToastKind = 'info' | 'success' | 'warning' | 'danger';

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  message?: string;
}

interface UiState {
  theme: 'light' | 'dark';
  toasts: Toast[];
  unreadNotifications: number;
  toggleTheme: () => void;
  toast: (kind: ToastKind, title: string, message?: string) => void;
  dismissToast: (id: number) => void;
  setUnread: (count: number) => void;
  applyBrandColor: (color?: string) => void;
}

let toastId = 0;

function initialTheme(): 'light' | 'dark' {
  const saved = localStorage.getItem('lsx-theme');
  if (saved === 'light' || saved === 'dark') return saved;
  return 'dark'; // tenant module defaults to dark mode
}

export const useUiStore = create<UiState>((set, get) => ({
  theme: initialTheme(),
  toasts: [],
  unreadNotifications: 0,
  toggleTheme: () => {
    const theme = get().theme === 'dark' ? 'light' : 'dark';
    localStorage.setItem('lsx-theme', theme);
    set({ theme });
  },
  toast: (kind, title, message) => {
    const id = ++toastId;
    const toasts = [...get().toasts, { id, kind, title, message }].slice(-5);
    set({ toasts });
    setTimeout(() => get().dismissToast(id), kind === 'danger' ? 7000 : 4000);
  },
  dismissToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
  setUnread: (count) => set({ unreadNotifications: count }),
  /** Apply a brand color (hex) to the platform CSS tokens. Pass `undefined` to clear. */
  applyBrandColor: (color?: string) => {
    const root = document.documentElement;
    const c = color || '#4f46e5';
    root.style.setProperty('--primary', c);
    root.style.setProperty('--primary-hover', c);
    // Keep the "soft" token a *tint* of the brand color rather than the color
    // itself: badges/selection backgrounds use --primary-soft with --primary
    // text, so making them identical hides the text (e.g. the "Admin" badge).
    root.style.setProperty('--primary-soft', `color-mix(in srgb, ${c} 14%, var(--surface))`);
    root.style.setProperty('--primary-gradient', c);
  },
}));
